import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import {
  type ActionsFactory,
  type ResettableStoreState,
  type StoreInstance,
  createStore,
  createStoreInstance,
} from '@seeqdev/utils';
import type { TreemapItem } from '@/treemap/TreemapChart.atom';

export type TreemapParent = { id?: string };

export type TreemapBreadcrumbs = { id: string; name: string }[];

export interface TreemapStatisticSelection {
  index: number;
  id?: string | number;
  key?: string | null;
}

export type TreemapConditionColors = Record<string, string | undefined>;

export interface TreemapState {
  /** The asset the treemap swaps out for each of its nodes. Set from the first condition's asset. */
  swap: Partial<{ id: string; name: string }>;
  /** The asset whose children are the nodes of the treemap. */
  parent: TreemapParent;
  /** The assets from the root down to `parent`, used to navigate back up the hierarchy. */
  breadcrumbs: TreemapBreadcrumbs;
  /** The nodes of the treemap, one per child asset of `parent`. */
  tree: TreemapItem[];
  /** Translation key explaining why the treemap cannot be shown, or `undefined` once it has loaded. */
  helpKey: string | undefined;
  /** The statistics shown on each node, indexed by their position in the toolbar. Positions may be empty. */
  statistics: Array<TreemapStatisticSelection | undefined>;
  /** The priority colors, from highest to lowest priority. */
  priorityColors: string[];
  /** The color of nodes that match no priority. */
  neutralColor: string | undefined;
  /** Treemap-only color overrides for worksheet conditions, keyed by condition id. */
  conditionColors: TreemapConditionColors;
  /** The signal whose value sizes each node in the table builder treemap. */
  proportionBySignal: string | null | undefined;
  /** The column whose value sizes each node in the table builder treemap. */
  proportionByColumn: string | null | undefined;
  /** Whether every node in the table builder treemap has the same size. */
  isUniformSizing: boolean;
  /** Whether the table builder treemap sizes nodes inversely to their value. */
  treemapSquareScalingInverse: boolean;
}

export type TreemapDehydratedState = Pick<
  TreemapState,
  | 'parent'
  | 'statistics'
  | 'conditionColors'
  | 'proportionBySignal'
  | 'proportionByColumn'
  | 'isUniformSizing'
  | 'treemapSquareScalingInverse'
>;

export interface TreemapActions {
  /**
   * Sets the asset that is swapped out for each node. A different asset means a different hierarchy, so the parent
   * is cleared when the asset changes.
   */
  setSwap: (swap: { id: string; name: string }) => void;
  /** Sets the asset whose children are shown, clearing the breadcrumbs and nodes that belonged to the old parent. */
  setParent: (parent: TreemapParent) => void;
  /** Sets the assets from the root down to the parent, used to navigate back up the hierarchy. */
  setBreadcrumbs: (breadcrumbs: TreemapBreadcrumbs) => void;
  /** Sets the nodes of the treemap, which also clears any reason the treemap could not be shown. */
  setTree: (tree: TreemapItem[]) => void;
  /** Sets the translation key explaining why the treemap cannot be shown, such as when it has no conditions. */
  setHelpKey: (helpKey: string) => void;
  /** Merges a statistic into the one already at its `index`. */
  setStatistic: (statistic: TreemapStatisticSelection) => void;
  /** Sets the colors ordered by priority from highest to lowest, with the neutral color last. */
  setColors: (colors: string[]) => void;
  /** Sets the Treemap-only color for a condition, or removes it when `color` is `undefined`. */
  setConditionColor: (id: string, color: string | undefined) => void;
  /** Sets the signal whose value sizes each node in the table builder treemap, or `null` to clear it. */
  setProportionBySignal: (proportionBy: string | null) => void;
  /** Sets the column whose value sizes each node in the table builder treemap, or `null` to clear it. */
  setProportionByColumn: (proportionBy: string | null) => void;
  /** Sets whether every node in the table builder treemap has the same size. */
  setIsUniformSizing: (isUniformSizing: boolean) => void;
  /** Sets whether the table builder treemap sizes nodes inversely to their value. */
  setTreemapSquareScalingInverse: (inverse: boolean) => void;
  /** Drops the color overrides of items removed from the worksheet. */
  removeItems: (ids: string[]) => void;
  /**
   * Points statistics and color overrides at the swapped-in items when the details pane items are asset swapped.
   * `swaps` maps each swapped-out id to its swapped-in id.
   */
  swapItems: (swaps: Record<string, string>) => void;
  /** Restores the initial state, with any given fields overriding it. */
  reset: ResettableStoreState<TreemapState>;
}

const initialState: TreemapState = Object.freeze({
  swap: {},
  parent: {},
  breadcrumbs: [],
  tree: [],
  helpKey: undefined,
  statistics: [],
  priorityColors: [],
  neutralColor: undefined,
  conditionColors: {},
  proportionBySignal: undefined,
  proportionByColumn: undefined,
  isUniformSizing: false,
  treemapSquareScalingInverse: false,
});

const treemapActionsFactory: ActionsFactory<TreemapState, TreemapActions> = (set, _get, resetState) => ({
  setSwap: (swap) =>
    set((draft) => {
      if (Object.keys(draft.swap).length > 0 && swap.id !== draft.swap.id) {
        draft.parent = {};
      }
      draft.swap = swap;
    }),

  setParent: (parent) =>
    set((draft) => {
      draft.parent = parent;
      draft.breadcrumbs = [];
      draft.tree = [];
    }),

  setBreadcrumbs: (breadcrumbs) =>
    set((draft) => {
      draft.breadcrumbs = breadcrumbs;
    }),

  setTree: (tree) =>
    set((draft) => {
      draft.tree = tree;
      draft.helpKey = undefined;
    }),

  setHelpKey: (helpKey) =>
    set((draft) => {
      draft.helpKey = helpKey;
    }),

  setStatistic: (statistic) =>
    set((draft) => {
      draft.statistics[statistic.index] = { ...draft.statistics[statistic.index], ...statistic };
    }),

  setColors: (colors) =>
    set((draft) => {
      draft.priorityColors = colors.slice(0, -1);
      draft.neutralColor = colors.at(-1);
    }),

  setConditionColor: (id, color) =>
    set((draft) => {
      if (color === undefined) {
        delete draft.conditionColors[id];
      } else {
        draft.conditionColors[id] = color;
      }
    }),

  setProportionBySignal: (proportionBy) =>
    set((draft) => {
      draft.proportionBySignal = proportionBy;
    }),

  setProportionByColumn: (proportionBy) =>
    set((draft) => {
      draft.proportionByColumn = proportionBy;
    }),

  setIsUniformSizing: (isUniformSizing) =>
    set((draft) => {
      draft.isUniformSizing = isUniformSizing;
    }),

  setTreemapSquareScalingInverse: (inverse) =>
    set((draft) => {
      draft.treemapSquareScalingInverse = inverse;
    }),

  removeItems: (ids) =>
    set((draft) => {
      ids.forEach((id) => delete draft.conditionColors[id]);
    }),

  swapItems: (swaps) =>
    set((draft) => {
      draft.statistics.forEach((statistic) => {
        const swappedId = statistic?.id === undefined ? undefined : swaps[statistic.id];
        if (statistic && swappedId) {
          statistic.id = swappedId;
        }
      });

      Object.entries(swaps).forEach(([id, swappedId]) => {
        if (id in draft.conditionColors) {
          draft.conditionColors[swappedId] = draft.conditionColors[id];
          delete draft.conditionColors[id];
        }
      });
    }),

  reset: resetState,
});

/** The state saved in worksteps. Everything else is fetched again when the treemap loads. */
export const dehydrateTreemapState = (state: TreemapState): TreemapDehydratedState => ({
  parent: state.parent,
  statistics: state.statistics,
  conditionColors: state.conditionColors,
  proportionBySignal: state.proportionBySignal,
  proportionByColumn: state.proportionByColumn,
  isUniformSizing: state.isUniformSizing,
  treemapSquareScalingInverse: state.treemapSquareScalingInverse,
});

/**
 * Loads persisted state: everything returns to its defaults except the priority colors, which only a forced reset
 * clears, and then the persisted state is applied. Merging alone would keep the breadcrumbs and nodes of the previous
 * parent, and `fetchTreemap()` only fetches breadcrumbs when there are none.
 */
export const rehydrateTreemapState = (
  store: Pick<TreemapStore, 'getState' | 'setState'>,
  dehydratedState: Partial<TreemapDehydratedState>,
) => {
  const { priorityColors, neutralColor } = store.getState();
  store.setState({ ...initialState, priorityColors, neutralColor, ...dehydratedState }, true);
};

const STORE_NAME = 'Treemap';

const [TreemapStoreProvider, useTreemapStoreInstance, useTreemapSelector] = createStore(
  STORE_NAME,
  initialState,
  treemapActionsFactory,
);

export { TreemapStoreProvider };

export const useTreemapSwap = () => useTreemapSelector((state) => state.swap);
export const useTreemapParent = () => useTreemapSelector((state) => state.parent);
export const useTreemapBreadcrumbs = () => useTreemapSelector((state) => state.breadcrumbs);
export const useTreemapTree = () => useTreemapSelector((state) => state.tree);
export const useTreemapHelpKey = () => useTreemapSelector((state) => state.helpKey);
export const useTreemapStatistics = () => useTreemapSelector((state) => state.statistics);
export const useTreemapPriorityColors = () => useTreemapSelector((state) => state.priorityColors);
export const useTreemapConditionColors = () => useTreemapSelector((state) => state.conditionColors);
export const useTreemapProportionBySignal = () => useTreemapSelector((state) => state.proportionBySignal);
export const useTreemapProportionByColumn = () => useTreemapSelector((state) => state.proportionByColumn);
export const useTreemapIsUniformSizing = () => useTreemapSelector((state) => state.isUniformSizing);
export const useTreemapSquareScalingInverse = () => useTreemapSelector((state) => state.treemapSquareScalingInverse);

export const useTreemapActions = () => useTreemapStoreInstance().actions;

export type TreemapStore = StoreInstance<TreemapState, TreemapActions>;

/**
 * Creates a new store instance for tests that must assert exact store state or when
 * porting Flux store tests that call actions imperatively.
 *
 * Prefer `<TestStoreProvider>` or `<*StoreProvider>` in tests and validate behavior
 * through UI interactions + DOM assertions whenever possible.
 *
 * NOTE: Only use this function in **test files**.
 *
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestTreemapStore(overrides: Partial<TreemapState> = {}): TreemapStore {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestTreemapStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, treemapActionsFactory);
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateTreemapStore(initialStateOverrides?: Partial<TreemapState>): TreemapStore {
  return createStoreInstance<TreemapState, TreemapActions>(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    treemapActionsFactory,
    { storeName: STORE_NAME },
  );
}

/**
 * Registers the Treemap store with the persistence system.
 * Uses the legacy store name 'sqTreemapStore' for workstep compatibility.
 */
export const usePersistTreemapStore = () => {
  const register = useRegisterPersistedStore();
  const store = useTreemapStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqTreemapStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateTreemapState,
      rehydrate: (dehydratedState: Partial<TreemapDehydratedState>) => rehydrateTreemapState(store, dehydratedState),
    });
  }, [register, store]);
};
